import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Brand } from '@/components/layout/Brand';
import { EnvironmentBadge } from '@/components/layout/EnvironmentBadge';
import { UserMenu } from '@/components/layout/UserMenu';
import { ErrorState, Button } from '@/components/ui';
import { listCategorySummaries, useQuery } from '@/data';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { CATEGORY_ICONS } from './categoryIcons';

export function CategoriesPage() {
  const { data, error, loading, reload } = useQuery(listCategorySummaries, []);

  return (
    <div className="min-h-screen">
      <header className="flex h-topbar items-center gap-3 border-b border-line bg-surface px-4 lg:px-6">
        <Brand />
        <div className="ml-auto flex items-center gap-3">
          <EnvironmentBadge />
          <span aria-hidden="true" className="hidden h-6 w-px bg-line sm:block" />
          <UserMenu showSettings={false} />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:py-16">
        <div className="mb-10 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Select Business Category</h1>
          <p className="mt-2 text-sm text-ink-secondary">Choose the business type you want to manage</p>
        </div>

        {error ? (
          <ErrorState message={error.message} action={<Button variant="secondary" onClick={reload}>Try again</Button>} />
        ) : (
          <ul
            className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            aria-busy={loading}
          >
            {(data ?? Array.from({ length: 4 }, () => null)).map((category, index) => {
              if (!category) {
                return (
                  <li key={index} className="card h-[220px] animate-pulse p-6" aria-hidden="true">
                    <div className="h-12 w-12 rounded-lg bg-surface-sunken" />
                    <div className="mt-5 h-4 w-28 rounded bg-surface-sunken" />
                    <div className="mt-3 h-3 w-full rounded bg-surface-sunken" />
                    <div className="mt-2 h-3 w-2/3 rounded bg-surface-sunken" />
                  </li>
                );
              }
              const Icon = CATEGORY_ICONS[category.code];
              const to = category.moduleAvailable ? `/${category.slug}` : `/categories/${category.slug}`;
              return (
                <li key={category.code}>
                  <Link
                    to={to}
                    className="card group flex h-full flex-col p-6 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-overlay focus-visible:shadow-focus focus-visible:outline-none"
                  >
                    <span
                      className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-lg transition-colors',
                        category.moduleAvailable
                          ? 'bg-primary-soft text-primary'
                          : 'bg-surface-sunken text-ink-secondary group-hover:bg-primary-soft group-hover:text-primary',
                      )}
                    >
                      <Icon size={24} aria-hidden="true" />
                    </span>
                    <h2 className="mt-5 text-base font-semibold text-ink">{category.name}</h2>
                    <p className="mt-1 text-[13px] text-ink-secondary">
                      <span className="font-medium text-ink">{formatNumber(category.customerCount)}</span> customer
                      {category.customerCount === 1 ? '' : 's'}
                    </p>
                    <p className="mt-2 line-clamp-2 text-[13px] leading-snug text-ink-muted">{category.description}</p>
                    <div className="mt-auto flex items-center justify-between pt-5 text-[13px]">
                      {category.moduleAvailable ? (
                        <span className="font-medium text-primary">Manage</span>
                      ) : (
                        <span className="text-ink-muted">Coming soon</span>
                      )}
                      <ArrowRight
                        size={16}
                        aria-hidden="true"
                        className="text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                      />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-10 text-center text-xs text-ink-muted">
          Counts are Central Admin business records. Open a category to manage linked Production operations.
        </p>
      </main>
    </div>
  );
}
